import React, { useState, useEffect } from "react";

import {
  fetchConfigs,
  deleteConfig,
  activateConfig
} from '../../pages/AssistConfig/helper';
import { Button } from "../ui/button";
import ActionModal from '../ActionModal';
import NotificationDialog from "../NotificationDialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Plus,
  Edit,
  Trash2,
  Check
} from "lucide-react";

const NotificationConfiguration = () => {
  const [configsData, setConfigsData] = useState([]);
	const [needToUpdate, setNeedToUpdate] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedConfigData, setSelectedConfigData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isActiveModalOpen, setIsActiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (needToUpdate) {
      fetchConfigs(setConfigsData, setIsLoading);
      setNeedToUpdate(false);
    }
  }, [needToUpdate]);

  const onRejectButtonClick = () => {
    setIsDeleteModalOpen(false);
    setSelectedConfig(null);
  };

  const handleCreateNotification = () => {
    setSelectedConfigData(null);
    setIsEditModalOpen(true);
  };

  const handleEditNotification = (notification) => {
    setSelectedConfigData(notification);
    setIsEditModalOpen(true);
  };

  const handleDeleteNotification = (notification) => {
    setSelectedConfig(notification.id);
    setIsDeleteModalOpen(true);
  };

  const toggleNotificationStatus = (notification) => {
    setSelectedConfig(notification.id);
    setSelectedConfigData(notification);
    setIsActiveModalOpen(true);
  };

  const onActivateRejectButtonClick = () => {
    setIsActiveModalOpen(false);
    setSelectedConfig(null);
  }

  const onConfirmButtonClick = async () => {
    await deleteConfig(
      selectedConfig,
      configsData,
      setConfigsData,
      setIsLoading,
      onRejectButtonClick
    );

    setNeedToUpdate(true);
  };

  const onActivateButtonClick = async () => {
    await activateConfig(
      selectedConfig,
      !selectedConfigData?.active,
      setNeedToUpdate,
      setIsLoading,
      onActivateRejectButtonClick
    );
  };

  const handleSaveNotification = (notificationData) => {
    // setIsEditModalOpen(true);
    // if (editingNotification) {} else {}
    // setEditingNotification(undefined);
  };

  return (
    <Card className="border rounded-lg shadow-sm">
      <CardHeader className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <CardTitle className="text-xl font-bold">
            Notification Configuration
          </CardTitle>
          <CardDescription className="mt-2">
            Configure where and when AI assistance will be offered to users
          </CardDescription>
          <p className="text-sm text-gray-500 mt-2">
            Set up rules to determine when users see AI support pop-ups
            based on the pages they visit.
          </p>
        </div>
        <div className="flex-shrink-0">
          <Button 
            onClick={handleCreateNotification} 
            className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Notification Rule
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Notification Name</TableHead>
                <TableHead>Page URL</TableHead>
                <TableHead>Active?</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configsData?.configs && configsData?.configs.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell className="font-medium">
                    {notification.name}
                  </TableCell>
                  <TableCell className="max-w-[250px] truncate">
                    {notification?.triggerUrl.length > 0 && (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${notification.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                        data-tooltip-id="table-tooltip"
                        data-tooltip-html={notification.triggerUrl.join('<br />')}
                        data-tooltip-place="top"
                      >
                        {notification?.triggerUrl[0]}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span >
                      {notification.active ? 'Yes' : 'No'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditNotification(notification)}
                        className="h-8 w-8 p-0"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteNotification(notification)}
                        className="h-8 w-8 p-0"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleNotificationStatus(notification)}
                        className={`h-8 w-8 p-0 ${notification.active ? 'bg-green-50' : ''}`}
                        title={notification.active ? "Deactivate" : "Activate"}
                      >
                        <Check className={`h-4 w-4 ${notification.active ? 'text-green-600' : ''}`} />
                        <span className="sr-only">
                          {notification.active ? 'Deactivate' : 'Activate'}
                        </span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {isDeleteModalOpen && (
        <ActionModal
          isOpen={isDeleteModalOpen}
          confirm={onConfirmButtonClick}
          confirmLabel="Delete"
          reject={onRejectButtonClick}
          rejectLabel="Cancel"
          headerText={`Delete AI Notification Trigger?`}
        >
        <div className='mt-2'>
          Deleting this notification configuration is permanent.
          All associated data will be permanently removed and cannot be
          recovered. Please confirm that you want to proceed, as this action
          cannot be undone.
        </div>
        </ActionModal>
      )}

      {isActiveModalOpen && (
        <ActionModal
          isOpen={isActiveModalOpen}
          confirm={onActivateButtonClick}
          confirmLabel={selectedConfigData?.active ? 'Deactivate' : 'Activate'}
          reject={onActivateRejectButtonClick}
          rejectLabel="Cancel"
          headerText={`${selectedConfigData?.active ? 'Deactivate' : 'Activate'} AI Notification Trigger?`}
        >
          <div className='mt-2'>
            Changing the status of this notification configuration will
            immediately affect its behavior. Deactivating will disable all
            associated alerts, while activating will enable them. Please
            confirm that you want to proceed, as this action takes effect
            immediately.
          </div>
        </ActionModal>
      )}

      <NotificationDialog
        open={isEditModalOpen}
        onSave={handleSaveNotification}
        notification={selectedConfigData}
        setNotification={setSelectedConfigData}
        setNeedToUpdate={setNeedToUpdate}
        notificationDialogOpen={setIsEditModalOpen}
      />
    </Card>
  );
};

export default NotificationConfiguration;
